import type { TicketFilterRequest } from '../types/tickets';
import type { CustomerFilterRequest } from '../types/customers';
import type { UserFilterRequest } from '../types/users';
import type { NotificationFilterRequest } from '../types/notifications';
import type { AutomationRuleFilterRequest, AutomationRuleLogFilterRequest } from '../types/automationRules';

export const queryKeys = {
  // Auth
  auth: {
    user: ['auth', 'user'] as const,
  },

  // Tickets
  tickets: {
    all: ['tickets'] as const,
    lists: () => [...queryKeys.tickets.all, 'list'] as const,
    list: (filters: TicketFilterRequest) => [...queryKeys.tickets.lists(), filters] as const,
    allOrgsLists: () => [...queryKeys.tickets.all, 'all-orgs'] as const,
    allOrgsList: (filters: TicketFilterRequest) => [...queryKeys.tickets.allOrgsLists(), filters] as const,
    details: () => [...queryKeys.tickets.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.tickets.details(), id] as const,
    stats: () => [...queryKeys.tickets.all, 'stats'] as const,
    comments: (ticketId: string) => [...queryKeys.tickets.detail(ticketId), 'comments'] as const,
    history: (ticketId: string) => [...queryKeys.tickets.detail(ticketId), 'history'] as const,
    participants: (ticketId: string) => [...queryKeys.tickets.detail(ticketId), 'participants'] as const,
    followers: (ticketId: string) => [...queryKeys.tickets.detail(ticketId), 'followers'] as const,
    articleLinks: (ticketId: string) => [...queryKeys.tickets.detail(ticketId), 'articleLinks'] as const,
    // Trashed tickets (admins) - kept outside lists() so list invalidations don't refetch it
    trash: (organizationId?: string) => [...queryKeys.tickets.all, 'trash', organizationId] as const,
    articleSuggest: (ticketId: string, query: string) => [...queryKeys.tickets.detail(ticketId), 'articleSuggest', query] as const,
    mergePreview: (sourceTicketId: string, targetTicketId: string) => [...queryKeys.tickets.detail(sourceTicketId), 'mergePreview', targetTicketId] as const,
  },

  // Ticket categories. The active-only list (pickers) and the with-archived list (management, filters, reports)
  // never share a cache entry; both sit under list(organizationId), so invalidating that refreshes both.
  categories: {
    all: ['categories'] as const,
    lists: () => [...queryKeys.categories.all, 'list'] as const,
    list: (organizationId?: string) => [...queryKeys.categories.lists(), organizationId] as const,
    listVariant: (organizationId: string | undefined, includeArchived: boolean) =>
      [...queryKeys.categories.list(organizationId), includeArchived ? 'with-archived' : 'active'] as const,
    detail: (id: string) => [...queryKeys.categories.all, 'detail', id] as const,
  },

  // Knowledge base collections (and their sections)
  articleCollections: {
    all: ['articleCollections'] as const,
    list: (organizationId?: string) => [...queryKeys.articleCollections.all, 'list', organizationId] as const,
  },

  // Articles
  articles: {
    all: ['articles'] as const,
    details: () => [...queryKeys.articles.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.articles.details(), id] as const,
    ticketLinks: (articleId: string) => [...queryKeys.articles.detail(articleId), 'ticketLinks'] as const,
    revisions: (articleId: string) => [...queryKeys.articles.detail(articleId), 'revisions'] as const,
    revision: (articleId: string, revisionId: string) => [...queryKeys.articles.revisions(articleId), revisionId] as const,
    draft: (articleId: string) => [...queryKeys.articles.detail(articleId), 'draft'] as const,
    needsAttention: (organizationIds?: string[]) => [...queryKeys.articles.all, 'needs-attention', organizationIds] as const,
    searchInsights: (organizationIds: string[] | undefined, days: number) =>
      [...queryKeys.articles.all, 'search-insights', organizationIds, days] as const,
  },

  // Customers
  customers: {
    all: ['customers'] as const,
    lists: () => [...queryKeys.customers.all, 'list'] as const,
    list: (filters?: CustomerFilterRequest) => [...queryKeys.customers.lists(), filters] as const,
    details: () => [...queryKeys.customers.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.customers.details(), id] as const,
  },

  // Users
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters?: UserFilterRequest) => [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
    // Tickets a member is assigned to that aren't closed, in one organization
    openTicketCount: (id: string, organizationId?: string) =>
      [...queryKeys.users.detail(id), 'open-ticket-count', organizationId] as const,
  },

  // Reports
  reports: {
    all: ['reports'] as const,
    dashboard: () => [...queryKeys.reports.all, 'dashboard'] as const,
    dashboardAllOrgs: (organizationIds?: string[]) => [...queryKeys.reports.all, 'dashboard-all-orgs', organizationIds] as const,
    trends: (params?: { fromDate?: string; toDate?: string }) =>
      [...queryKeys.reports.all, 'trends', params] as const,
    categories: (organizationId: string, fromDate: string, toDate: string) =>
      [...queryKeys.reports.all, 'categories', organizationId, fromDate, toDate] as const,
  },

  // Profile
  profile: {
    all: ['profile'] as const,
    adminOrganizations: () => [...queryKeys.profile.all, 'admin-organizations'] as const,
  },

  // Notifications
  notifications: {
    all: ['notifications'] as const,
    lists: () => [...queryKeys.notifications.all, 'list'] as const,
    list: (filters?: NotificationFilterRequest) => [...queryKeys.notifications.lists(), filters] as const,
    count: () => [...queryKeys.notifications.all, 'count'] as const,
    details: () => [...queryKeys.notifications.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.notifications.details(), id] as const,
  },

  // Automation Rules
  automationRules: {
    all: ['automationRules'] as const,
    lists: () => [...queryKeys.automationRules.all, 'list'] as const,
    list: (filters?: AutomationRuleFilterRequest, organizationId?: string) =>
      [...queryKeys.automationRules.lists(), organizationId, filters] as const,
    details: () => [...queryKeys.automationRules.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.automationRules.details(), id] as const,
    logs: (ruleId: string) => [...queryKeys.automationRules.detail(ruleId), 'logs'] as const,
    logList: (ruleId: string, filters?: AutomationRuleLogFilterRequest) => [...queryKeys.automationRules.logs(ruleId), filters] as const,
  },

  // Held emails (from archived customers)
  heldEmails: {
    all: ['heldEmails'] as const,
    list: (organizationId?: string) => [...queryKeys.heldEmails.all, 'list', organizationId] as const,
  },
} as const;

// Helper functions for invalidation
export const invalidateQueries = {
  tickets: {
    all: () => queryKeys.tickets.all,
    lists: () => queryKeys.tickets.lists(),
    allOrgsLists: () => queryKeys.tickets.allOrgsLists(),
    detail: (id: string) => queryKeys.tickets.detail(id),
    stats: () => queryKeys.tickets.stats(),
  },
  customers: {
    all: () => queryKeys.customers.all,
    lists: () => queryKeys.customers.lists(),
    detail: (id: string) => queryKeys.customers.detail(id),
  },
  users: {
    all: () => queryKeys.users.all,
    lists: () => queryKeys.users.lists(),
    detail: (id: string) => queryKeys.users.detail(id),
  },
  auth: {
    user: () => queryKeys.auth.user,
  },
  categories: {
    all: () => queryKeys.categories.all,
    list: (organizationId?: string) => queryKeys.categories.list(organizationId),
  },
  articleCollections: {
    all: () => queryKeys.articleCollections.all,
    list: (organizationId?: string) => queryKeys.articleCollections.list(organizationId),
  },
  reports: {
    all: () => queryKeys.reports.all,
    dashboard: () => queryKeys.reports.dashboard(),
    dashboardAllOrgs: () => [...queryKeys.reports.all, 'dashboard-all-orgs'],
  },
  notifications: {
    all: () => queryKeys.notifications.all,
    lists: () => queryKeys.notifications.lists(),
    count: () => queryKeys.notifications.count(),
    detail: (id: string) => queryKeys.notifications.detail(id),
  },
  automationRules: {
    all: () => queryKeys.automationRules.all,
    lists: () => queryKeys.automationRules.lists(),
    detail: (id: string) => queryKeys.automationRules.detail(id),
    logs: (ruleId: string) => queryKeys.automationRules.logs(ruleId),
  },
};